import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientesAPI, tiposClientesAPI, gruposAPI, provinciasAPI, localidadesAPI, whatsappTemplatesAPI, movimientosAPI, whatsappEnviosAPI, usuariosAPI, whatsappBaileysAPI, comunicacionesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { WhatsAppServicioPanel } from '../components/WhatsAppServicioPanel';
import { PlantillaImagenEditor } from '../components/PlantillaImagenEditor';
import {
  readUseBaileysFromStorage,
  writeUseBaileysToStorage,
  readSendRateProfileFromStorage,
  sendWhatsAppSequential,
  abrirWhatsappWebEscalonado,
} from '../utils/whatsappBaileys';
import { assertHorarioUsoCliente } from '../hooks/useHorariosUso';
import { formatFechaSistema } from '../utils/fechasSistema';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { ExportActions } from '../components/ExportActions';
import {
  exportFromColumnas,
  valorCeldaClienteExport,
  imprimirListadoColumnas,
} from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { aplicarPlantillaGrupo, DEFAULT_PLANTILLA_GRUPO } from '../utils/plantillasGrupo';
import '../Clientes.css';

const CLAVE_PREFERENCIAS = 'clientes_listado';
const CLAVE_FILTROS_CLIENTES = 'clientes_listado_filtros';
const COLUMNAS_DEFINICION = [
  { id: 'cuit', label: 'CUIT' },
  { id: 'razon_social', label: 'Razón Social' },
  { id: 'estado', label: 'Estado' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'grupo', label: 'Grupo' },
  { id: 'abono_mensual', label: 'Abono mensual' },
  { id: 'empleados', label: 'Empleados' },
  { id: 'direccion', label: 'Dirección' },
  { id: 'provincia', label: 'Provincia' },
  { id: 'localidad', label: 'Localidad' },
  { id: 'telefono', label: 'Teléfono' },
  { id: 'condicion_impositiva', label: 'Cond. impositiva' },
  { id: 'ingresos_brutos', label: 'Ing. brutos' },
  { id: 'drg', label: 'DRG' },
  { id: 'dv', label: 'DV' },
  { id: 'iva_liquida', label: 'IVA líquida' },
  { id: 'saldo', label: 'Saldo' },
];
const defaultColumnasVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisibles = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultOrdenColumnas = COLUMNAS_DEFINICION.map(c => c.id);
const defaultFiltrosValores = {
  cuit: '', razon_social: '', razon_social_prefix: false, activo: 'true', tipo_id: '', grupo_id: '', direccion: '', provincia_id: '', localidad_id: '',
  telefono: '', abono_mensual_min: '', cantidad_empleados: '', saldo_min: '', saldo_max: '',
  condicion_impositiva: '', ingresos_brutos: '', drg: '', dv: '', iva_liquida: '',
};

const getSortValue = (cliente, columnId) => {
  switch (columnId) {
    case 'cuit': return (cliente.cuit || '').toString().toLowerCase();
    case 'razon_social': return (cliente.razon_social || '').toString().toLowerCase();
    case 'estado': return cliente.activo === true ? 1 : 0;
    case 'tipo': return (cliente.tipo_nombre || '').toString().toLowerCase();
    case 'grupo': return (cliente.grupo_nombre || '').toString().toLowerCase();
    case 'abono_mensual': return parseFloat(cliente.abono_mensual) || 0;
    case 'empleados': return parseInt(cliente.cantidad_empleados, 10) || 0;
    case 'direccion': return (cliente.direccion || '').toString().toLowerCase();
    case 'provincia': return (cliente.provincia_nombre || '').toString().toLowerCase();
    case 'localidad': return (cliente.localidad_nombre || '').toString().toLowerCase();
    case 'telefono': return (cliente.telefono || '').toString().toLowerCase();
    case 'condicion_impositiva': return (cliente.condicion_impositiva || '').toString().toLowerCase();
    case 'ingresos_brutos': return (cliente.ingresos_brutos || '').toString().toLowerCase();
    case 'drg': return (cliente.drg || '').toString().toLowerCase();
    case 'dv': return (cliente.dv || '').toString().toLowerCase();
    case 'iva_liquida': return parseInt(cliente.iva_liquida, 10) || 0;
    case 'saldo': return parseFloat(cliente.saldo) || 0;
    default: return '';
  }
};

const formatearTelefono = (telefono) => {
  if (!telefono) return '';
  let n = String(telefono).replace(/[\s\-()]/g, '');
  if (n.startsWith('0')) n = n.substring(1);
  if (!n.startsWith('+') && !n.startsWith('54')) n = '54' + n;
  if (!n.startsWith('+')) n = '+' + n;
  return n;
};

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatFecha = (f) => formatFechaSistema(f);

const aplicarPlantillaResumen = (cliente, texto, fechaSaldo) => {
  const telefono = cliente.celular || cliente.telefono || '';
  const saldo = parseFloat(cliente.saldo) != null ? Number(cliente.saldo).toFixed(2) : '0.00';
  const fechaSaldoFmt = fechaSaldo ? formatFecha(fechaSaldo) : '';
  return texto
    .replace(/{razon_social}/g, cliente.razon_social || '')
    .replace(/{cuit}/g, cliente.cuit || '')
    .replace(/{saldo}/g, saldo)
    .replace(/{fecha_saldo}/g, fechaSaldoFmt)
    .replace(/{telefono}/g, telefono)
    .replace(/{tipo_nombre}/g, cliente.tipo_nombre || '')
    .replace(/{direccion}/g, cliente.direccion || '')
    .replace(/{email}/g, cliente.email || '')
    .replace(/{provincia_nombre}/g, cliente.provincia_nombre || '')
    .replace(/{localidad_nombre}/g, cliente.localidad_nombre || '');
};

const buildListaMovimientos = (movs) => {
  if (!Array.isArray(movs) || movs.length === 0) return 'Sin movimientos en el período.';
  return movs.map(m => {
    const fecha = formatFecha(m.fecha);
    const debe = m.tipo_movimiento === 'DEBITO' ? `$${formatMoneda(m.monto)}` : '-';
    const haber = m.tipo_movimiento === 'CREDITO' ? `$${formatMoneda(m.monto)}` : '-';
    return `${fecha} | ${(m.concepto || '').slice(0, 40)} | Debe: ${debe} | Haber: ${haber} | Saldo: $${formatMoneda(m.saldo_acumulado)}`;
  }).join('\n');
};

const aplicarPlantillaResumenMovimientos = (cliente, movimientos, fecha_desde, fecha_hasta, texto) => {
  const saldo = movimientos.length > 0 ? formatMoneda(movimientos[movimientos.length - 1].saldo_acumulado) : (parseFloat(cliente.saldo) != null ? Number(cliente.saldo).toFixed(2) : '0.00');
  const lista_movimientos = buildListaMovimientos(movimientos);
  return texto
    .replace(/{razon_social}/g, cliente.razon_social || '')
    .replace(/{cuit}/g, cliente.cuit || '')
    .replace(/{saldo}/g, saldo)
    .replace(/{lista_movimientos}/g, lista_movimientos)
    .replace(/{fecha_desde}/g, fecha_desde || 'Todo')
    .replace(/{fecha_hasta}/g, fecha_hasta || 'Todo')
    .replace(/{tipo_nombre}/g, cliente.tipo_nombre || '')
    .replace(/{telefono}/g, cliente.celular || cliente.telefono || '')
    .replace(/{direccion}/g, cliente.direccion || '')
    .replace(/{email}/g, cliente.email || '')
    .replace(/{provincia_nombre}/g, cliente.provincia_nombre || '')
    .replace(/{localidad_nombre}/g, cliente.localidad_nombre || '');
};

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Evita pantalla completa "Cargando..." en cada refetch por filtros (mantiene foco en los inputs). */
  const primeraCargaClientesRef = useRef(true);
  const [refrescandoClientes, setRefrescandoClientes] = useState(false);
  const cargaClientesSeqRef = useRef(0);
  const [tipos, setTipos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [filtrosValores, setFiltrosValores] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CLAVE_FILTROS_CLIENTES);
      if (saved) {
        return { ...defaultFiltrosValores, ...JSON.parse(saved) };
      }
    } catch (e) {}
    return { ...defaultFiltrosValores };
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showModalWhatsApp, setShowModalWhatsApp] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState('');
  const [mensajePersonalizado, setMensajePersonalizado] = useState('');
  const [plantillasGrupo, setPlantillasGrupo] = useState([]);
  const [plantillaGrupoSaldoId, setPlantillaGrupoSaldoId] = useState('');
  const [mensajeGrupoSaldo, setMensajeGrupoSaldo] = useState(DEFAULT_PLANTILLA_GRUPO);
  const [plantillaGrupoMovId, setPlantillaGrupoMovId] = useState('');
  const [mensajeGrupoMov, setMensajeGrupoMov] = useState(DEFAULT_PLANTILLA_GRUPO);
  const [previewGrupoSaldo, setPreviewGrupoSaldo] = useState('');
  const [previewGrupoMov, setPreviewGrupoMov] = useState('');
  const [loadingPreviewGrupo, setLoadingPreviewGrupo] = useState(false);
  const [showModalWhatsAppMovimientos, setShowModalWhatsAppMovimientos] = useState(false);
  const [mensajePersonalizadoMovimientos, setMensajePersonalizadoMovimientos] = useState('');
  const [plantillaSeleccionadaIdMovimientos, setPlantillaSeleccionadaIdMovimientos] = useState('');
  const [fechaDesdeMov, setFechaDesdeMov] = useState('');
  const [fechaHastaMov, setFechaHastaMov] = useState('');
  const [guardarEnHistorialMov, setGuardarEnHistorialMov] = useState(true);
  const [enviandoMasivoMov, setEnviandoMasivoMov] = useState(false);
  const [waUseBaileys, setWaUseBaileys] = useState(() => readUseBaileysFromStorage());
  const [waSendRateProfile, setWaSendRateProfile] = useState(() => readSendRateProfileFromStorage());
  const [waEnvioProgreso, setWaEnvioProgreso] = useState('');
  const [enviandoMasivoWa, setEnviandoMasivoWa] = useState(false);
  const [modoEnvioSaldo, setModoEnvioSaldo] = useState('individual');
  const [modoEnvioMov, setModoEnvioMov] = useState('individual');
  const [waStatus, setWaStatus] = useState(null);
  const [waStatusLoading, setWaStatusLoading] = useState(false);
  const [showModalAbono, setShowModalAbono] = useState(false);
  const [abonoModo, setAbonoModo] = useState('porcentaje'); // 'porcentaje' | 'fijo'
  const [abonoValor, setAbonoValor] = useState('');
  const [actualizandoAbono, setActualizandoAbono] = useState(false);
  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisibles }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisibles }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnas]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);
  const { hasPermission, puedeEnviarGrupos } = useAuth();
  const { fechaHoy } = useZonaHoraria();
  const { logoUrl } = useBranding();
  const puedeModoGrupo = puedeEnviarGrupos();
  const [fechaSaldoWa, setFechaSaldoWa] = useState('');
  const [previewSaldoWa, setPreviewSaldoWa] = useState(null);

  useEffect(() => {
    setFechaSaldoWa((prev) => prev || fechaHoy());
  }, [fechaHoy]);

  const colVisible = (id) => columnasVisibles[id] !== false;
  const filtroVisible = useCallback((colId) => filtrosVisibles[colId] !== false && columnasVisibles[colId] !== false, [filtrosVisibles, columnasVisibles]);

  const setFiltro = (key, value) => setFiltrosValores(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    sessionStorage.setItem(CLAVE_FILTROS_CLIENTES, JSON.stringify(filtrosValores));
  }, [filtrosValores]);

  const loadClientes = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaClientesRef.current;
    const seq = ++cargaClientesSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoClientes(true);
      const params = {};
      if (filtroVisible('cuit') && filtrosValores.cuit) params.cuit = filtrosValores.cuit;
      if (filtroVisible('razon_social') && filtrosValores.razon_social) {
        params.razon_social = filtrosValores.razon_social;
        params.razon_social_modo = filtrosValores.razon_social_prefix ? 'prefix' : 'contains';
      }
      if (filtroVisible('estado')) params.activo = filtrosValores.activo;
      else params.activo = 'true';
      if (filtroVisible('tipo') && filtrosValores.tipo_id) params.tipo_id = filtrosValores.tipo_id;
      if (filtroVisible('grupo') && filtrosValores.grupo_id) params.grupo_id = filtrosValores.grupo_id;
      if (filtroVisible('direccion') && filtrosValores.direccion) params.direccion = filtrosValores.direccion;
      if (filtroVisible('provincia') && filtrosValores.provincia_id) params.provincia_id = filtrosValores.provincia_id;
      if (filtroVisible('localidad') && filtrosValores.localidad_id) params.localidad_id = filtrosValores.localidad_id;
      if (filtroVisible('telefono') && filtrosValores.telefono) params.telefono = filtrosValores.telefono;
      if (filtroVisible('condicion_impositiva') && filtrosValores.condicion_impositiva) params.condicion_impositiva = filtrosValores.condicion_impositiva;
      if (filtroVisible('ingresos_brutos') && filtrosValores.ingresos_brutos) params.ingresos_brutos = filtrosValores.ingresos_brutos;
      if (filtroVisible('drg') && filtrosValores.drg) params.drg = filtrosValores.drg;
      if (filtroVisible('dv') && filtrosValores.dv) params.dv = filtrosValores.dv;
      if (filtroVisible('iva_liquida') && filtrosValores.iva_liquida !== '') params.iva_liquida = filtrosValores.iva_liquida;
      if (filtroVisible('abono_mensual') && filtrosValores.abono_mensual_min !== '') params.abono_mensual_min = filtrosValores.abono_mensual_min;
      if (filtroVisible('empleados') && filtrosValores.cantidad_empleados !== '') params.cantidad_empleados = filtrosValores.cantidad_empleados;
      if (filtroVisible('saldo') && filtrosValores.saldo_min !== '') params.saldo_min = filtrosValores.saldo_min;
      if (filtroVisible('saldo') && filtrosValores.saldo_max !== '') params.saldo_max = filtrosValores.saldo_max;
      const response = await clientesAPI.getAll(params);
      if (response.success) {
        setClientes(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaClientesRef.current = false;
      }
      if (seq === cargaClientesSeqRef.current) {
        setRefrescandoClientes(false);
      }
    }
  }, [filtrosValores, filtroVisible]);

  const loadTipos = useCallback(async () => {
    try {
      const response = await tiposClientesAPI.getAll();
      if (response.success) setTipos(response.data);
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  }, []);

  const loadGrupos = useCallback(async () => {
    try {
      const response = await gruposAPI.getAll();
      if (response.success) setGrupos(response.data);
    } catch (error) {
      console.error('Error al cargar grupos:', error);
      toast.error(error.message || 'Error al cargar grupos');
    }
  }, []);

  const loadProvincias = useCallback(async () => {
    try {
      const response = await provinciasAPI.getAll();
      if (response.success) setProvincias(response.data);
    } catch (error) {
      console.error('Error al cargar provincias:', error);
    }
  }, []);

  const loadLocalidades = useCallback(async (provinciaId) => {
    if (!provinciaId) {
      setLocalidades([]);
      return;
    }
    try {
      const response = await localidadesAPI.getByProvincia(provinciaId);
      if (response.success) setLocalidades(response.data);
      else setLocalidades([]);
    } catch (error) {
      setLocalidades([]);
    }
  }, []);

  useEffect(() => {
    loadClientes();
    loadTipos();
    loadGrupos();
    loadProvincias();
  }, [loadClientes, loadTipos, loadGrupos, loadProvincias]);

  useEffect(() => {
    loadLocalidades(filtrosValores.provincia_id);
  }, [filtrosValores.provincia_id, loadLocalidades]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(CLAVE_PREFERENCIAS);
        if (cancelled) return;
        const raw = res.data;
        const data =
          raw?.valor ??
          (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.columnasVisibles != null ? raw : null) ??
          (Array.isArray(raw) ? raw[0]?.valor : null);
        if (data && typeof data === 'object') {
          if (data.columnasVisibles && typeof data.columnasVisibles === 'object') {
            setColumnasVisibles({ ...defaultColumnasVisibles, ...data.columnasVisibles });
          }
          if (data.filtrosVisibles && typeof data.filtrosVisibles === 'object') {
            setFiltrosVisibles({ ...defaultFiltrosVisibles, ...data.filtrosVisibles });
          }
          if (Array.isArray(data.ordenColumnas) && data.ordenColumnas.length > 0) {
            const knownIds = new Set(COLUMNAS_DEFINICION.map((c) => c.id));
            const saved = data.ordenColumnas.filter((id) => knownIds.has(id));
            const missing = defaultOrdenColumnas.filter((id) => !saved.includes(id));
            setOrdenColumnas([...saved, ...missing]);
          }
          // filtrosValores: solo sessionStorage (sobrevive "Volver a clientes" / acciones).
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const guardarPreferenciasDesdeLista = useCallback(async (snapshot) => {
    const payload = {
      columnasVisibles: snapshot?.columnasVisibles
        ? { ...snapshot.columnasVisibles }
        : { ...columnasVisibles },
      filtrosVisibles: snapshot?.filtrosVisibles
        ? { ...snapshot.filtrosVisibles }
        : { ...filtrosVisibles },
      ordenColumnas: snapshot?.ordenColumnas ? [...snapshot.ordenColumnas] : [...ordenColumnas],
    };
    if (snapshot?.columnasVisibles) setColumnasVisibles(payload.columnasVisibles);
    if (snapshot?.filtrosVisibles) setFiltrosVisibles(payload.filtrosVisibles);
    if (snapshot?.ordenColumnas) setOrdenColumnas(payload.ordenColumnas);
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(CLAVE_PREFERENCIAS, payload);
      toast.success('Preferencias de columnas y filtros guardadas.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar preferencias');
    } finally {
      setSavingPreferencias(false);
    }
  }, [columnasVisibles, filtrosVisibles, ordenColumnas]);

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => columnasVisibles[id] !== false),
    [ordenColumnas, columnasVisibles]
  );

  const clientesOrdenados = useMemo(() => {
    if (!sortByColumn) return clientes;
    const a = [...clientes];
    a.sort((c1, c2) => {
      const v1 = getSortValue(c1, sortByColumn);
      const v2 = getSortValue(c2, sortByColumn);
      const isNum = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (isNum) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return a;
  }, [clientes, sortByColumn, sortAsc]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc(prev => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const getColumnLabel = (columnId) => COLUMNAS_DEFINICION.find(c => c.id === columnId)?.label || columnId;

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: 'clientes',
        sheetName: 'Clientes',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: clientesOrdenados,
        getCellValue: (c, colId) => valorCeldaClienteExport(c, colId),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDFListado = () => {
    try {
      imprimirListadoColumnas({
        titulo: 'Listado de clientes',
        subtitulo: `Generado el ${formatFecha(fechaHoy())}`,
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: clientesOrdenados,
        getCellValue: (c, colId) => valorCeldaClienteExport(c, colId),
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const loadPlantillas = useCallback(async () => {
    try {
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
      if (res.success && Array.isArray(res.data)) setPlantillas(res.data);
    } catch (e) {
      setPlantillas([]);
      console.error('Error al cargar plantillas cliente:', e);
    }
  }, []);

  const loadPlantillasGrupo = useCallback(async () => {
    try {
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'grupo' });
      if (res.success && Array.isArray(res.data)) {
        setPlantillasGrupo(res.data);
        return res.data;
      }
      setPlantillasGrupo([]);
      return [];
    } catch (e) {
      setPlantillasGrupo([]);
      console.error('Error al cargar plantillas grupo:', e);
      return [];
    }
  }, []);

  const refreshWaStatus = useCallback(async () => {
    if (!waUseBaileys) return;
    setWaStatusLoading(true);
    try {
      const s = await whatsappBaileysAPI.getStatus();
      setWaStatus(s);
    } catch (e) {
      setWaStatus({ reachable: false, connected: false, serviceError: e.message });
    } finally {
      setWaStatusLoading(false);
    }
  }, [waUseBaileys]);

  const handleToggleWaBaileys = (v) => {
    writeUseBaileysToStorage(v);
    setWaUseBaileys(v);
    if (v) {
      setTimeout(() => refreshWaStatus(), 0);
    }
  };

  useEffect(() => {
    if (showModalWhatsApp && waUseBaileys) refreshWaStatus();
  }, [showModalWhatsApp, waUseBaileys, refreshWaStatus]);

  useEffect(() => {
    if (showModalWhatsAppMovimientos && waUseBaileys) refreshWaStatus();
  }, [showModalWhatsAppMovimientos, waUseBaileys, refreshWaStatus]);

  const toggleSeleccion = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clientesActivos = clientes.filter(c => c.activo);
  const seleccionarTodos = () => setSelectedIds(new Set(clientesActivos.map(c => c.id)));
  const deseleccionarTodos = () => setSelectedIds(new Set());

  const clientesSeleccionados = useMemo(
    () => clientes.filter((c) => selectedIds.has(c.id)),
    [clientes, selectedIds]
  );

  const canalPermiteWhatsApp = (c) =>
    !c.canal_comunicacion || c.canal_comunicacion === 'whatsapp' || c.canal_comunicacion === 'ambos';

  const canalPermiteEmail = (c) =>
    c.canal_comunicacion === 'email' || c.canal_comunicacion === 'ambos';

  const esDestinatarioGrupo = (c) => Number(c.recibe_comunicacion_grupo) === 1;

  /** Envío individual: solo clientes sin grupo. */
  const clientesIndividualConTelefono = useMemo(
    () => clientesSeleccionados.filter(
      (c) => !c.grupo_id && canalPermiteWhatsApp(c) && (c.celular || c.telefono)
    ),
    [clientesSeleccionados]
  );

  const clientesIndividualConEmail = useMemo(
    () => clientesSeleccionados.filter(
      (c) => !c.grupo_id && canalPermiteEmail(c) && c.email
    ),
    [clientesSeleccionados]
  );

  /** Envío por grupo: solo clientes con grupo en la selección. */
  const clientesGrupoEnSeleccion = useMemo(
    () => clientesSeleccionados.filter((c) => c.grupo_id),
    [clientesSeleccionados]
  );

  /** Destinatarios de comunicación del grupo que están seleccionados. */
  const clientesGrupoDestinatariosSeleccionados = useMemo(
    () => clientesSeleccionados.filter((c) => c.grupo_id && esDestinatarioGrupo(c)),
    [clientesSeleccionados]
  );

  const gruposEnvioInfo = useMemo(() => {
    const map = new Map();
    clientesGrupoEnSeleccion.forEach((c) => {
      const gid = String(c.grupo_id);
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push(c);
    });
    let gruposConDestinatario = 0;
    for (const [, miembros] of map) {
      if (miembros.some((c) => esDestinatarioGrupo(c))) gruposConDestinatario++;
    }
    return { map, gruposConDestinatario };
  }, [clientesGrupoEnSeleccion]);

  const clientesGrupoDestinatariosConTelefono = useMemo(
    () => clientesGrupoDestinatariosSeleccionados.filter(
      (c) => canalPermiteWhatsApp(c) && (c.celular || c.telefono)
    ),
    [clientesGrupoDestinatariosSeleccionados]
  );

  const puedeAbrirEnvioWhatsApp = useMemo(() => {
    if (clientesIndividualConTelefono.length > 0) return true;
    if (puedeModoGrupo && gruposEnvioInfo.gruposConDestinatario > 0) return true;
    return false;
  }, [clientesIndividualConTelefono.length, puedeModoGrupo, gruposEnvioInfo.gruposConDestinatario]);

  const resolverModoEnvioInicial = useCallback(() => {
    if (clientesIndividualConTelefono.length > 0) return 'individual';
    if (puedeModoGrupo && gruposEnvioInfo.gruposConDestinatario > 0) return 'grupo';
    return 'individual';
  }, [clientesIndividualConTelefono.length, puedeModoGrupo, gruposEnvioInfo.gruposConDestinatario]);

  const explicarPorQueNoPuedeEnviarWhatsApp = useCallback(() => {
    if (selectedIds.size === 0) {
      return 'Seleccione al menos un cliente activo.';
    }
    const partes = [];
    const todosConGrupo =
      clientesSeleccionados.length > 0 && clientesSeleccionados.every((c) => c.grupo_id);
    const ningunDestinatarioGrupo = !clientesGrupoDestinatariosSeleccionados.length;
    const todosSinTelefono = clientesSeleccionados.every((c) => !c.celular && !c.telefono);
    const todosSoloEmail = clientesSeleccionados.every(
      (c) => c.canal_comunicacion === 'email'
    );

    if (todosConGrupo && ningunDestinatarioGrupo) {
      partes.push(
        'Los clientes seleccionados pertenecen a grupos pero ninguno está marcado como destinatario en Clientes → Grupos → Configurar envío.'
      );
    }
    if (todosSinTelefono) {
      partes.push('Ningún cliente seleccionado tiene teléfono o celular cargado.');
    } else if (todosSoloEmail) {
      partes.push('Los clientes seleccionados tienen canal de comunicación solo email (sin WhatsApp).');
    }
    if (
      !puedeModoGrupo &&
      clientesGrupoDestinatariosSeleccionados.length > 0 &&
      clientesIndividualConTelefono.length === 0
    ) {
      partes.push('Su usuario no tiene permiso para enviar resúmenes por grupo (enviar_grupos).');
    }
    if (partes.length === 0) {
      partes.push(
        'Ningún cliente seleccionado puede recibir WhatsApp. Revise grupo, teléfono, canal de comunicación y destinatarios del grupo.'
      );
    }
    return partes.join(' ');
  }, [
    selectedIds.size,
    clientesSeleccionados,
    clientesGrupoDestinatariosSeleccionados.length,
    clientesIndividualConTelefono.length,
    puedeModoGrupo,
  ]);

  const abrirModalWhatsApp = useCallback(async () => {
    if (selectedIds.size === 0) {
      toast.warning('Seleccione al menos un cliente activo.');
      return;
    }
    if (!puedeAbrirEnvioWhatsApp) {
      toast.warning(explicarPorQueNoPuedeEnviarWhatsApp());
      return;
    }
    try {
      await loadPlantillas();
      const listaGrupo = await loadPlantillasGrupo();
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
      if (res.success && res.data?.length > 0) {
        const primera = res.data[0];
        setPlantillaSeleccionadaId(primera.id);
        setMensajePersonalizado(primera.texto || '');
      } else {
        setPlantillaSeleccionadaId('');
        setMensajePersonalizado(
          `Hola {razon_social},\n\nResumen de cuenta:\nCUIT: {cuit}\nSaldo: \${saldo}\nTipo: {tipo_nombre}\n\nVariables: razon_social, cuit, saldo, telefono, tipo_nombre, direccion, email, provincia_nombre, localidad_nombre.`
        );
      }
      if (listaGrupo?.length > 0) {
        setPlantillaGrupoSaldoId(listaGrupo[0].id);
        setMensajeGrupoSaldo(listaGrupo[0].texto || DEFAULT_PLANTILLA_GRUPO);
      } else {
        setPlantillaGrupoSaldoId('');
        setMensajeGrupoSaldo(DEFAULT_PLANTILLA_GRUPO);
      }
      setModoEnvioSaldo(resolverModoEnvioInicial());
      setShowModalWhatsApp(true);
    } catch (e) {
      toast.error(e.message || 'No se pudo abrir el envío. Revise plantillas WhatsApp.');
    }
  }, [
    selectedIds.size,
    puedeAbrirEnvioWhatsApp,
    explicarPorQueNoPuedeEnviarWhatsApp,
    loadPlantillas,
    loadPlantillasGrupo,
    resolverModoEnvioInicial,
  ]);

  const resumenExclusionGrupoIndividual = () => {
    const conGrupo = clientesSeleccionados.filter((c) => c.grupo_id);
    if (conGrupo.length === 0) return null;
    return `${conGrupo.length} cliente(s) con grupo se omiten en envío individual. Use el modo «Por grupo» para incluirlos.`;
  };

  const resumenExclusionGrupoModoGrupo = () => {
    const sinGrupo = clientesSeleccionados.filter((c) => !c.grupo_id);
    const conGrupoSinDestinatario = clientesGrupoEnSeleccion.filter((c) => !esDestinatarioGrupo(c));
    const partes = [];
    if (sinGrupo.length > 0) {
      partes.push(`${sinGrupo.length} cliente(s) sin grupo se omiten.`);
    }
    if (conGrupoSinDestinatario.length > 0) {
      partes.push(`${conGrupoSinDestinatario.length} integrante(s) de grupo no son destinatarios y no recibirán el mensaje.`);
    }
    return partes.length > 0 ? partes.join(' ') : null;
  };

  const enviarGruposConsolidado = async ({ tipo, fechaDesde, fechaHasta, saldoAl, onProgressLabel, plantillaTexto, templateId }) => {
    const { map, gruposConDestinatario } = gruposEnvioInfo;
    const sinGrupoEnSeleccion = clientesSeleccionados.filter((c) => !c.grupo_id);

    if (map.size === 0) {
      toast.warning('Ningún cliente seleccionado pertenece a un grupo.');
      return false;
    }
    if (gruposConDestinatario === 0) {
      toast.warning('Ningún cliente seleccionado es destinatario de comunicaciones de su grupo. Configure en Clientes → Grupos → Configurar envío.');
      return false;
    }
    if (sinGrupoEnSeleccion.length > 0) {
      toast.info(`${sinGrupoEnSeleccion.length} cliente(s) sin grupo se omiten en envío por grupo.`);
    }

    const idsDestinatariosSeleccionados = new Set(clientesGrupoDestinatariosSeleccionados.map((c) => c.id));
    const itemsWa = [];
    let emailsOk = 0;

    let gruposConError = 0;
    for (const [, miembros] of map.entries()) {
      const destinatariosEnGrupo = miembros.filter((c) => esDestinatarioGrupo(c));
      const grupoId = miembros[0].grupo_id;
      const nombreGrupo = miembros[0].grupo_nombre || `grupo #${grupoId}`;

      if (destinatariosEnGrupo.length === 0) {
        toast.info(`${nombreGrupo}: ningún integrante seleccionado es destinatario; se omite.`);
        continue;
      }

      let res;
      try {
        res = await gruposAPI.armarMensaje(grupoId, {
          tipo,
          cliente_ids: miembros.map((m) => m.id),
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined,
          saldo_al: saldoAl || undefined,
        });
      } catch (e) {
        gruposConError++;
        const msg = e.message || 'Error al armar mensaje del grupo';
        toast.error(`${nombreGrupo}: ${msg}. Configure destinatarios en Clientes → Grupos → Configurar envío y guarde.`);
        continue;
      }
      if (!res.success || !res.data) {
        gruposConError++;
        toast.error(res.message || `Error al armar mensaje de ${nombreGrupo}`);
        continue;
      }
      const { variables, destinatarios, grupo } = res.data;
      const plantilla = plantillaTexto || DEFAULT_PLANTILLA_GRUPO;
      for (const dest of destinatarios) {
        if (!idsDestinatariosSeleccionados.has(dest.id)) continue;
        const mensajeFinal = aplicarPlantillaGrupo(plantilla, variables, dest);
        const canal = dest.canal_comunicacion || 'whatsapp';
        if ((canal === 'whatsapp' || canal === 'ambos') && dest.telefono) {
          itemsWa.push({
            phone: formatearTelefono(dest.telefono).replace('+', ''),
            caption: mensajeFinal,
            templateId: templateId || undefined,
            clienteId: dest.id,
            destinatario: dest.razon_social,
          });
        }
        if ((canal === 'email' || canal === 'ambos') && dest.email) {
          try {
            await comunicacionesAPI.sendEmail({
              to: dest.email,
              subject: tipo === 'movimientos'
                ? `Movimientos de grupo - ${grupo?.nombre || ''}`
                : `Estado de cuenta - ${grupo?.nombre || ''}`,
              text: mensajeFinal,
              fileName: `grupo-${grupo?.id || grupoId}.pdf`,
            });
            emailsOk++;
          } catch (err) {
            console.error('Email grupo fallido:', dest.razon_social, err);
          }
        }
      }
    }

    if (itemsWa.length === 0 && emailsOk === 0) {
      if (gruposConError > 0) {
        toast.error('No se pudo enviar ningún grupo. Marque al menos un integrante como destinatario y pulse Guardar en Configurar envío.');
      } else {
        toast.error('No hay destinatarios con contacto válido. Revise Clientes → Grupos → Configurar envío.');
      }
      return false;
    }

    if (itemsWa.length > 0) {
      if (waUseBaileys) {
        if (!waStatus?.reachable) {
          toast.error('No se alcanza el servicio WhatsApp.');
          return false;
        }
        if (!waStatus?.connected) {
          toast.warning('WhatsApp no está conectado.');
          return false;
        }
        onProgressLabel?.('Iniciando envío por grupo…');
        const { ok, fail, abortedEarly, abortMessage } = await sendWhatsAppSequential(itemsWa, {
          sendRateProfile: waSendRateProfile,
          onProgress: (p) => onProgressLabel?.(p.phase || `Enviado ${p.index}/${p.total}`),
        });
        if (abortedEarly && abortMessage) {
          toast.warning(`${abortMessage} Enviados: ${ok}. Fallidos: ${fail}.`);
        } else {
          toast.success(`Enviados ${ok} mensaje(s) por grupo${fail ? `; ${fail} con error` : ''}.`);
        }
      } else {
        abrirWhatsappWebEscalonado(
          itemsWa.map((it) => ({ numero: it.phone, texto: it.caption })),
          1500
        );
        toast.success(`Se abrirán ${itemsWa.length} conversación(es) de WhatsApp (por grupo).`);
      }
    }
    if (emailsOk > 0) {
      toast.success(`Enviados ${emailsOk} email(s) de grupo.`);
    }
    return true;
  };

  const seleccionarPlantilla = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaSeleccionadaId(Number.isFinite(id) ? id : '');
    const p = plantillas.find(x => x.id === id);
    if (p) setMensajePersonalizado(p.texto || '');
  };

  const guardarComoNueva = async () => {
    const nombre = window.prompt('Nombre de la plantilla:', `Resumen ${plantillas.length + 1}`);
    if (!nombre?.trim()) return;
    try {
      const res = await whatsappTemplatesAPI.create({ nombre: nombre.trim(), texto: mensajePersonalizado, tipo: 'cliente' });
      if (res.success) {
        toast.success('Plantilla guardada');
        await loadPlantillas();
        setPlantillaSeleccionadaId(res.data.id);
      }
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar la plantilla');
    }
  };

  const actualizarPlantilla = async () => {
    if (!plantillaSeleccionadaId) return;
    try {
      const p = plantillas.find(x => x.id === plantillaSeleccionadaId);
      if (!p) return;
      await whatsappTemplatesAPI.update(plantillaSeleccionadaId, { nombre: p.nombre, texto: mensajePersonalizado, tipo: 'cliente' });
      toast.success('Plantilla actualizada');
      await loadPlantillas();
    } catch (e) {
      toast.error(e.message || 'No se pudo actualizar');
    }
  };

  const eliminarPlantilla = async () => {
    if (!plantillaSeleccionadaId) return;
    const p = plantillas.find(x => x.id === plantillaSeleccionadaId);
    if (!p || !window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try {
      await whatsappTemplatesAPI.delete(plantillaSeleccionadaId);
      toast.success('Plantilla eliminada');
      await loadPlantillas();
      const rest = plantillas.filter(x => x.id !== plantillaSeleccionadaId);
      setPlantillaSeleccionadaId(rest[0]?.id || '');
      setMensajePersonalizado(rest[0]?.texto || '');
    } catch (e) {
      toast.error(e.message || 'No se pudo eliminar');
    }
  };

  const seleccionarPlantillaGrupoSaldo = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaGrupoSaldoId(Number.isFinite(id) ? id : '');
    const p = plantillasGrupo.find((x) => x.id === id);
    if (p) setMensajeGrupoSaldo(p.texto || DEFAULT_PLANTILLA_GRUPO);
  };

  const seleccionarPlantillaGrupoMov = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaGrupoMovId(Number.isFinite(id) ? id : '');
    const p = plantillasGrupo.find((x) => x.id === id);
    if (p) setMensajeGrupoMov(p.texto || DEFAULT_PLANTILLA_GRUPO);
  };

  const guardarPlantillaGrupo = async (mensaje, setId) => {
    const nombre = window.prompt('Nombre de la plantilla de grupo:', `Grupo ${plantillasGrupo.length + 1}`);
    if (!nombre?.trim()) return;
    try {
      const res = await whatsappTemplatesAPI.create({ nombre: nombre.trim(), texto: mensaje, tipo: 'grupo' });
      if (res.success) {
        toast.success('Plantilla de grupo guardada');
        await loadPlantillasGrupo();
        setId(res.data.id);
      }
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar la plantilla');
    }
  };

  const actualizarPlantillaGrupo = async (id, mensaje) => {
    if (!id) return;
    try {
      const p = plantillasGrupo.find((x) => x.id === id);
      if (!p) return;
      await whatsappTemplatesAPI.update(id, { nombre: p.nombre, texto: mensaje, tipo: 'grupo' });
      toast.success('Plantilla de grupo actualizada');
      await loadPlantillasGrupo();
    } catch (e) {
      toast.error(e.message || 'No se pudo actualizar');
    }
  };

  const eliminarPlantillaGrupo = async (id, setId, setMensaje) => {
    if (!id) return;
    const p = plantillasGrupo.find((x) => x.id === id);
    if (!p || !window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try {
      await whatsappTemplatesAPI.delete(id);
      toast.success('Plantilla eliminada');
      const rest = await loadPlantillasGrupo();
      setId(rest[0]?.id || '');
      setMensaje(rest[0]?.texto || DEFAULT_PLANTILLA_GRUPO);
    } catch (e) {
      toast.error(e.message || 'No se pudo eliminar');
    }
  };

  const obtenerPrimerGrupoPreview = useCallback(() => {
    for (const [, miembros] of gruposEnvioInfo.map) {
      const dest = miembros.find((c) => esDestinatarioGrupo(c));
      if (dest) return { miembros, dest };
    }
    return null;
  }, [gruposEnvioInfo]);

  const cargarPreviewGrupo = useCallback(async (tipo, plantillaTexto, fechaDesde, fechaHasta, saldoAl, setPreview) => {
    const previewData = obtenerPrimerGrupoPreview();
    if (!previewData) {
      setPreview('');
      return;
    }
    const { miembros, dest } = previewData;
    const grupoId = miembros[0].grupo_id;
    setLoadingPreviewGrupo(true);
    try {
      const res = await gruposAPI.armarMensaje(grupoId, {
        tipo,
        cliente_ids: miembros.map((m) => m.id),
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
        saldo_al: saldoAl || undefined,
      });
      if (res.success && res.data?.variables) {
        setPreview(aplicarPlantillaGrupo(plantillaTexto, res.data.variables, dest));
      } else {
        setPreview('');
      }
    } catch {
      setPreview('');
    } finally {
      setLoadingPreviewGrupo(false);
    }
  }, [obtenerPrimerGrupoPreview]);

  useEffect(() => {
    if (!showModalWhatsApp || modoEnvioSaldo !== 'grupo' || !puedeModoGrupo) return;
    cargarPreviewGrupo('saldo', mensajeGrupoSaldo, null, null, fechaSaldoWa, setPreviewGrupoSaldo);
  }, [showModalWhatsApp, modoEnvioSaldo, mensajeGrupoSaldo, fechaSaldoWa, puedeModoGrupo, gruposEnvioInfo, cargarPreviewGrupo]);

  useEffect(() => {
    if (!showModalWhatsApp || modoEnvioSaldo !== 'individual' || !fechaSaldoWa) {
      setPreviewSaldoWa(null);
      return;
    }
    const first = clientesIndividualConTelefono[0];
    if (!first) {
      setPreviewSaldoWa(null);
      return;
    }
    let cancelled = false;
    clientesAPI.saldosAl([first.id], fechaSaldoWa)
      .then((res) => {
        if (cancelled || !res.success) return;
        const saldo = res.data?.[0]?.saldo;
        setPreviewSaldoWa({ ...first, saldo: saldo ?? first.saldo });
      })
      .catch(() => setPreviewSaldoWa(first));
    return () => { cancelled = true; };
  }, [showModalWhatsApp, modoEnvioSaldo, fechaSaldoWa, clientesIndividualConTelefono]);

  const obtenerClientesConSaldoAl = async (clientes) => {
    if (!clientes.length || !fechaSaldoWa) return clientes;
    const res = await clientesAPI.saldosAl(clientes.map((c) => c.id), fechaSaldoWa);
    if (!res.success) throw new Error(res.message || 'No se pudieron actualizar los saldos');
    const map = new Map((res.data || []).map((r) => [r.id, r.saldo]));
    return clientes.map((c) => ({ ...c, saldo: map.get(c.id) ?? c.saldo }));
  };

  useEffect(() => {
    if (!showModalWhatsAppMovimientos || modoEnvioMov !== 'grupo' || !puedeModoGrupo) return;
    cargarPreviewGrupo('movimientos', mensajeGrupoMov, fechaDesdeMov, fechaHastaMov, null, setPreviewGrupoMov);
  }, [showModalWhatsAppMovimientos, modoEnvioMov, mensajeGrupoMov, fechaDesdeMov, fechaHastaMov, puedeModoGrupo, gruposEnvioInfo, cargarPreviewGrupo]);

  const iniciarEnvioMasivo = async () => {
    try {
      await assertHorarioUsoCliente();
    } catch (e) {
      toast.error(e.message);
      return;
    }

    if (modoEnvioSaldo === 'grupo' && puedeModoGrupo) {
      setEnviandoMasivoWa(true);
      try {
        const ok = await enviarGruposConsolidado({
          tipo: 'saldo',
          saldoAl: fechaSaldoWa,
          onProgressLabel: setWaEnvioProgreso,
          plantillaTexto: mensajeGrupoSaldo,
          templateId: plantillaGrupoSaldoId,
        });
        if (ok) setShowModalWhatsApp(false);
      } finally {
        setEnviandoMasivoWa(false);
        setWaEnvioProgreso('');
      }
      return;
    }

    if (clientesIndividualConTelefono.length === 0) {
      toast.warning('No hay clientes sin grupo con teléfono para envío individual.');
      return;
    }
    const omitidosGrupo = clientesSeleccionados.filter((c) => c.grupo_id).length;
    if (omitidosGrupo > 0) {
      toast.info(`${omitidosGrupo} cliente(s) con grupo omitidos en envío individual.`);
    }
    if (waUseBaileys) {
      if (!waStatus?.reachable) {
        toast.error('No se alcanza el servicio WhatsApp. Revise WHATSAPP_EMBEDDED o WHATSAPP_SERVICE_URL en el servidor.');
        return;
      }
      if (!waStatus?.connected) {
        toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor y pulse «Actualizar estado / QR».');
        return;
      }
      setEnviandoMasivoWa(true);
      setWaEnvioProgreso('Actualizando saldos…');
      try {
        const clientesConSaldo = await obtenerClientesConSaldoAl(clientesIndividualConTelefono);
        setWaEnvioProgreso('Iniciando envío con pausas anti-bloqueo…');
        const items = clientesConSaldo.map((cliente) => ({
          phone: formatearTelefono(cliente.celular || cliente.telefono).replace('+', ''),
          caption: aplicarPlantillaResumen(cliente, mensajePersonalizado, fechaSaldoWa),
          templateId: plantillaSeleccionadaId || undefined,
          clienteId: cliente.id,
          destinatario: cliente.razon_social,
        }));
        const { ok, fail, abortedEarly, abortMessage } = await sendWhatsAppSequential(items, {
          sendRateProfile: waSendRateProfile,
          onProgress: (p) =>
            setWaEnvioProgreso(p.phase || `Enviado ${p.index}/${p.total}`),
        });
        if (abortedEarly && abortMessage) {
          toast.warning(`${abortMessage} Enviados: ${ok}. Fallidos: ${fail}.`);
        } else {
          toast.success(`Enviados ${ok} mensaje(s) por WhatsApp${fail ? `; ${fail} con error` : ''}.`);
        }
        let emailsOk = 0;
        const clientesConSaldoEmail = await obtenerClientesConSaldoAl(clientesIndividualConEmail);
        for (const cliente of clientesConSaldoEmail) {
          try {
            const texto = aplicarPlantillaResumen(cliente, mensajePersonalizado, fechaSaldoWa);
            await comunicacionesAPI.sendEmail({
              to: cliente.email,
              subject: `Estado de cuenta - ${cliente.razon_social}`,
              text: texto,
              fileName: `resumen-${cliente.cuit || cliente.id}.pdf`,
            });
            emailsOk++;
          } catch (err) {
            console.error('Email fallido:', cliente.razon_social, err);
          }
        }
        if (emailsOk > 0) {
          toast.success(`Enviados ${emailsOk} email(s).`);
        }
        setShowModalWhatsApp(false);
        return;
      } catch (e) {
        toast.error(e.message || 'Error al enviar por Baileys');
        return;
      } finally {
        setEnviandoMasivoWa(false);
        setWaEnvioProgreso('');
      }
    }
    try {
      const clientesConSaldo = await obtenerClientesConSaldoAl(clientesIndividualConTelefono);
      abrirWhatsappWebEscalonado(
        clientesConSaldo.map((cliente) => ({
          numero: formatearTelefono(cliente.celular || cliente.telefono).replace('+', ''),
          texto: aplicarPlantillaResumen(cliente, mensajePersonalizado, fechaSaldoWa),
        })),
        1500
      );
    } catch (e) {
      toast.error(e.message || 'No se pudieron actualizar los saldos');
      return;
    }
    setShowModalWhatsApp(false);
    toast.success(`Se abrirán ${clientesIndividualConTelefono.length} conversación(es) de WhatsApp.`);
  };

  const textoDefaultResumenMovimientos = 'Hola {razon_social},\n\nResumen de movimientos de cuenta:\nCUIT: {cuit}\nPeríodo: {fecha_desde} a {fecha_hasta}\n\nDetalle:\n{lista_movimientos}\n\nSaldo actual: {saldo}\n\nSaludos.';

  const abrirModalWhatsAppMovimientos = useCallback(async () => {
    if (selectedIds.size === 0) {
      toast.warning('Seleccione al menos un cliente activo.');
      return;
    }
    if (!puedeAbrirEnvioWhatsApp) {
      toast.warning(explicarPorQueNoPuedeEnviarWhatsApp());
      return;
    }
    try {
      await loadPlantillas();
      const listaGrupo = await loadPlantillasGrupo();
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
      if (res.success && res.data?.length > 0) {
        const primera = res.data[0];
        setPlantillaSeleccionadaIdMovimientos(primera.id);
        setMensajePersonalizadoMovimientos(primera.texto || textoDefaultResumenMovimientos);
      } else {
        setPlantillaSeleccionadaIdMovimientos('');
        setMensajePersonalizadoMovimientos(textoDefaultResumenMovimientos);
      }
      if (listaGrupo?.length > 0) {
        setPlantillaGrupoMovId(listaGrupo[0].id);
        setMensajeGrupoMov(listaGrupo[0].texto || DEFAULT_PLANTILLA_GRUPO);
      } else {
        setPlantillaGrupoMovId('');
        setMensajeGrupoMov(DEFAULT_PLANTILLA_GRUPO);
      }
      setModoEnvioMov(resolverModoEnvioInicial());
      setShowModalWhatsAppMovimientos(true);
    } catch (e) {
      toast.error(e.message || 'No se pudo abrir el envío. Revise plantillas WhatsApp.');
    }
  }, [
    selectedIds.size,
    puedeAbrirEnvioWhatsApp,
    explicarPorQueNoPuedeEnviarWhatsApp,
    loadPlantillas,
    loadPlantillasGrupo,
    resolverModoEnvioInicial,
  ]);

  const seleccionarPlantillaMovimientos = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaSeleccionadaIdMovimientos(Number.isFinite(id) ? id : '');
    const p = plantillas.find(x => x.id === id);
    if (p) setMensajePersonalizadoMovimientos(p.texto || '');
  };

  const enviarResumenMovimientosMasivo = async () => {
    try {
      await assertHorarioUsoCliente();
    } catch (e) {
      toast.error(e.message);
      return;
    }

    if (modoEnvioMov === 'grupo' && puedeModoGrupo) {
      setEnviandoMasivoMov(true);
      try {
        const ok = await enviarGruposConsolidado({
          tipo: 'movimientos',
          fechaDesde: fechaDesdeMov,
          fechaHasta: fechaHastaMov,
          onProgressLabel: setWaEnvioProgreso,
          plantillaTexto: mensajeGrupoMov,
          templateId: plantillaGrupoMovId,
        });
        if (ok) setShowModalWhatsAppMovimientos(false);
      } finally {
        setEnviandoMasivoMov(false);
        setWaEnvioProgreso('');
      }
      return;
    }

    const lista = clientesIndividualConTelefono;
    if (lista.length === 0) {
      toast.warning('No hay clientes sin grupo con teléfono para envío individual.');
      return;
    }
    const omitidosGrupo = clientesSeleccionados.filter((c) => c.grupo_id).length;
    if (omitidosGrupo > 0) {
      toast.info(`${omitidosGrupo} cliente(s) con grupo omitidos en envío individual.`);
    }
    if (waUseBaileys) {
      if (!waStatus?.reachable) {
        toast.error('No se alcanza el servicio WhatsApp. Revise WHATSAPP_EMBEDDED o WHATSAPP_SERVICE_URL en el servidor.');
        return;
      }
      if (!waStatus?.connected) {
        toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor y pulse «Actualizar estado / QR».');
        return;
      }
    }
    setEnviandoMasivoMov(true);
    const params = {};
    if (fechaDesdeMov) params.fecha_desde = fechaDesdeMov;
    if (fechaHastaMov) params.fecha_hasta = fechaHastaMov;
    const itemsBaileys = [];
    try {
      for (let i = 0; i < lista.length; i++) {
        const cliente = lista[i];
        try {
          const res = await movimientosAPI.getByCliente(cliente.id, params);
          const movs = res.success && Array.isArray(res.data) ? res.data : [];
          const mensajeFinal = aplicarPlantillaResumenMovimientos(
            cliente,
            movs,
            fechaDesdeMov,
            fechaHastaMov,
            mensajePersonalizadoMovimientos
          );
          if (guardarEnHistorialMov) {
            try {
              await whatsappEnviosAPI.create({
                cliente_id: cliente.id,
                template_id: plantillaSeleccionadaIdMovimientos || null,
                mensaje_texto: mensajeFinal,
              });
            } catch (e) {
              console.error('No guardar historial:', e);
            }
          }
          const num = formatearTelefono(cliente.celular || cliente.telefono).replace('+', '');
          if (waUseBaileys) {
            itemsBaileys.push({
              phone: num,
              caption: mensajeFinal,
              templateId: plantillaSeleccionadaIdMovimientos || undefined,
              clienteId: cliente.id,
              destinatario: cliente.razon_social,
            });
          } else {
            const url = `https://wa.me/${num}?text=${encodeURIComponent(mensajeFinal)}`;
            window.open(url, '_blank');
            await new Promise((r) => setTimeout(r, 1800));
          }
        } catch (err) {
          toast.error(`Error con ${cliente.razon_social}: ${err.message || 'Error'}`);
        }
      }
      if (waUseBaileys && itemsBaileys.length > 0) {
        setWaEnvioProgreso('Iniciando envío con pausas anti-bloqueo…');
        const { ok, fail, abortedEarly, abortMessage } = await sendWhatsAppSequential(itemsBaileys, {
          sendRateProfile: waSendRateProfile,
          onProgress: (p) =>
            setWaEnvioProgreso(p.phase || `Enviado ${p.index}/${p.total}`),
        });
        if (abortedEarly && abortMessage) {
          toast.warning(`${abortMessage} Enviados: ${ok}. Fallidos: ${fail}.`);
        } else {
          toast.success(`Enviados ${ok} mensaje(s) con resumen${fail ? `; ${fail} con error` : ''}.`);
        }
      } else if (!waUseBaileys) {
        toast.success(`Se abrieron ${lista.length} conversación(es) de WhatsApp con resumen de movimientos.`);
      }
    } finally {
      setEnviandoMasivoMov(false);
      setWaEnvioProgreso('');
      setShowModalWhatsAppMovimientos(false);
    }
  };

  const aplicarAjusteAbono = async () => {
    const valor = parseFloat(String(abonoValor).replace(',', '.'));
    if (Number.isNaN(valor)) {
      toast.error('Ingrese un número válido para el ajuste de abono.');
      return;
    }
    if (clientesSeleccionados.length === 0) {
      toast.warning('No hay clientes seleccionados.');
      return;
    }
    setActualizandoAbono(true);
    try {
      for (const cliente of clientesSeleccionados) {
        const actual = parseFloat(cliente.abono_mensual || 0) || 0;
        let nuevo = actual;
        if (abonoModo === 'porcentaje') {
          nuevo = actual + (actual * valor / 100);
        } else {
          nuevo = actual + valor;
        }
        const nuevoRedondeado = Math.round(nuevo * 100) / 100;
        await clientesAPI.update(cliente.id, { abono_mensual: nuevoRedondeado });
      }
      toast.success(`Abono actualizado para ${clientesSeleccionados.length} cliente(s).`);
      setShowModalAbono(false);
      setAbonoValor('');
      await loadClientes();
    } catch (error) {
      toast.error(error.message || 'Error al actualizar el abono mensual.');
    } finally {
      setActualizandoAbono(false);
    }
  };

  const handleToggleActivo = async (cliente) => {
    const nuevoEstado = !cliente.activo;
    const texto = nuevoEstado ? '¿Activar este cliente?' : '¿Desactivar este cliente? No podrá usarlo en movimientos ni cargas hasta que lo reactive.';
    if (!window.confirm(texto)) return;
    try {
      const response = await clientesAPI.update(cliente.id, { activo: nuevoEstado });
      if (response.success) {
        toast.success(nuevoEstado ? 'Cliente activado' : 'Cliente desactivado');
        loadClientes();
      }
    } catch (error) {
      toast.error(error.message || 'Error al actualizar cliente');
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes clientes-con-listado pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Clientes</h1>
        {hasPermission('crear_clientes') && (
          <Link to="/clientes/nuevo" className="btn-primary">Nuevo Cliente</Link>
        )}
      </div>

      <div className="clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
        {filtroVisible('cuit') && (
          <input
            type="text"
            placeholder="CUIT"
            value={filtrosValores.cuit}
            onChange={(e) => setFiltro('cuit', e.target.value)}
            className="search-input"
            style={{ maxWidth: 140 }}
          />
        )}
        {filtroVisible('razon_social') && (
          <>
            <input
              type="text"
              placeholder="Razón social"
              value={filtrosValores.razon_social}
              onChange={(e) => setFiltro('razon_social', e.target.value)}
              className="search-input"
              style={{ maxWidth: 180 }}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }} title="Filtrar desde la primera letra (sin buscar en el medio)">
              <input
                type="checkbox"
                checked={!!filtrosValores.razon_social_prefix}
                onChange={(e) => setFiltro('razon_social_prefix', e.target.checked)}
              />
              Desde 1ª letra
            </label>
          </>
        )}
        {filtroVisible('estado') && (
          <select value={filtrosValores.activo} onChange={(e) => setFiltro('activo', e.target.value)} title="Estado">
            <option value="true">Solo activos</option>
            <option value="false">Solo inactivos</option>
            <option value="">Todos</option>
          </select>
        )}
        {filtroVisible('tipo') && (
          <select value={filtrosValores.tipo_id} onChange={(e) => setFiltro('tipo_id', e.target.value)}>
            <option value="">Tipo</option>
            {tipos.map(t => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        )}
        {filtroVisible('grupo') && (
          <select value={filtrosValores.grupo_id} onChange={(e) => setFiltro('grupo_id', e.target.value)}>
            <option value="">Grupo</option>
            {grupos.map(g => (
              <option key={g.id} value={g.id}>{g.nombre}</option>
            ))}
          </select>
        )}
        {filtroVisible('abono_mensual') && (
          <input
            type="number"
            placeholder="Abono mín."
            value={filtrosValores.abono_mensual_min}
            onChange={(e) => setFiltro('abono_mensual_min', e.target.value)}
            min="0"
            step="0.01"
            style={{ width: 100, padding: '6px 8px' }}
          />
        )}
        {filtroVisible('empleados') && (
          <input
            type="number"
            placeholder="Empleados"
            value={filtrosValores.cantidad_empleados}
            onChange={(e) => setFiltro('cantidad_empleados', e.target.value)}
            min="0"
            style={{ width: 90, padding: '6px 8px' }}
          />
        )}
        {filtroVisible('direccion') && (
          <input
            type="text"
            placeholder="Dirección"
            value={filtrosValores.direccion}
            onChange={(e) => setFiltro('direccion', e.target.value)}
            style={{ maxWidth: 140, padding: '6px 8px' }}
          />
        )}
        {filtroVisible('provincia') && (
          <select value={filtrosValores.provincia_id} onChange={(e) => { setFiltro('provincia_id', e.target.value); setFiltro('localidad_id', ''); }}>
            <option value="">Provincia</option>
            {provincias.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        )}
        {filtroVisible('localidad') && (
          <select value={filtrosValores.localidad_id} onChange={(e) => setFiltro('localidad_id', e.target.value)} disabled={!filtrosValores.provincia_id}>
            <option value="">Localidad</option>
            {localidades.map(l => (
              <option key={l.id} value={l.id}>{l.nombre}</option>
            ))}
          </select>
        )}
        {filtroVisible('telefono') && (
          <input
            type="text"
            placeholder="Teléfono"
            value={filtrosValores.telefono}
            onChange={(e) => setFiltro('telefono', e.target.value)}
            style={{ maxWidth: 120, padding: '6px 8px' }}
          />
        )}
        {filtroVisible('condicion_impositiva') && (
          <input type="text" placeholder="Cond. impositiva" value={filtrosValores.condicion_impositiva} onChange={(e) => setFiltro('condicion_impositiva', e.target.value)} style={{ maxWidth: 140, padding: '6px 8px' }} />
        )}
        {filtroVisible('ingresos_brutos') && (
          <input type="text" placeholder="Ing. brutos" value={filtrosValores.ingresos_brutos} onChange={(e) => setFiltro('ingresos_brutos', e.target.value)} style={{ maxWidth: 120, padding: '6px 8px' }} />
        )}
        {filtroVisible('drg') && (
          <input type="text" placeholder="DRG" value={filtrosValores.drg} onChange={(e) => setFiltro('drg', e.target.value)} style={{ maxWidth: 100, padding: '6px 8px' }} />
        )}
        {filtroVisible('dv') && (
          <input type="text" placeholder="DV" value={filtrosValores.dv} onChange={(e) => setFiltro('dv', e.target.value)} style={{ maxWidth: 80, padding: '6px 8px' }} />
        )}
        {filtroVisible('iva_liquida') && (
          <select value={filtrosValores.iva_liquida} onChange={(e) => setFiltro('iva_liquida', e.target.value)}>
            <option value="">IVA líquida</option>
            <option value="1">Sí</option>
            <option value="0">No</option>
          </select>
        )}
        {filtroVisible('saldo') && (
          <>
            <input
              type="number"
              placeholder="Saldo mín."
              value={filtrosValores.saldo_min}
              onChange={(e) => setFiltro('saldo_min', e.target.value)}
              step="0.01"
              style={{ width: 95, padding: '6px 8px' }}
            />
            <input
              type="number"
              placeholder="Saldo máx."
              value={filtrosValores.saldo_max}
              onChange={(e) => setFiltro('saldo_max', e.target.value)}
              step="0.01"
              style={{ width: 95, padding: '6px 8px' }}
            />
          </>
        )}
        </div>
        <div className="clientes-toolbar-acciones">
          <div className="clientes-toolbar-acciones-inner">
            <div className="toolbar-grupo toolbar-grupo--seleccion">
              <button
                type="button"
                className="btn-action-icon"
                onClick={() => setFiltrosValores({ ...defaultFiltrosValores })}
                title="Limpiar todos los filtros"
              >
                <span aria-hidden="true">🧹</span>
              </button>
              <button
                type="button"
                className="btn-action-icon"
                onClick={seleccionarTodos}
                title="Seleccionar todos los clientes activos"
              >
                <span aria-hidden="true">✅</span>
              </button>
              <button
                type="button"
                className="btn-action-icon"
                onClick={deseleccionarTodos}
                title="Deseleccionar todos los clientes"
              >
                <span aria-hidden="true">✖️</span>
              </button>
            </div>
            <span className="toolbar-grupo--separador" aria-hidden="true" />
            <div className="toolbar-grupo toolbar-grupo--listado">
              <SelectorColumnas
                columnas={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(columnasVisibles).filter(id => columnasVisibles[id])}
                ordenColumnas={ordenColumnas}
                onOrdenChange={setOrdenColumnas}
                onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
                onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisibles })}
                onSave={guardarPreferenciasDesdeLista}
                titulo="Columnas visibles"
                labelBoton="Columnas"
              />
              <SelectorFiltros
                filtros={COLUMNAS_DEFINICION}
                visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id])}
                onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
                onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisibles })}
                onSave={guardarPreferenciasDesdeLista}
                titulo="Filtros visibles"
                labelBoton="Filtros"
              />
            </div>
            {(hasPermission('ver_clientes') || hasPermission('editar_clientes')) && (
              <>
                <span className="toolbar-grupo--separador" aria-hidden="true" />
                <div className="toolbar-grupo toolbar-grupo--acciones-extra">
                  {hasPermission('ver_clientes') && (
                    <>
                      <button
                        type="button"
                        className={`btn-primary btn-wa-mini${selectedIds.size > 0 && !puedeAbrirEnvioWhatsApp ? ' btn-wa-inactive' : ''}`}
                        onClick={abrirModalWhatsApp}
                        title={
                          selectedIds.size === 0
                            ? 'Seleccione clientes para enviar saldo por WhatsApp'
                            : !puedeAbrirEnvioWhatsApp
                              ? explicarPorQueNoPuedeEnviarWhatsApp()
                              : `Enviar saldo por WhatsApp (${clientesIndividualConTelefono.length} individual / ${gruposEnvioInfo.gruposConDestinatario} grupo(s))`
                        }
                      >
                        <span className="btn-wa-icon" aria-hidden="true">📱</span>
                      </button>
                      <button
                        type="button"
                        className={`btn-primary btn-wa-mini${selectedIds.size > 0 && !puedeAbrirEnvioWhatsApp ? ' btn-wa-inactive' : ''}`}
                        onClick={abrirModalWhatsAppMovimientos}
                        title={
                          selectedIds.size === 0
                            ? 'Seleccione clientes para enviar resumen de movimientos'
                            : !puedeAbrirEnvioWhatsApp
                              ? explicarPorQueNoPuedeEnviarWhatsApp()
                              : `Enviar resumen de movimientos (${clientesIndividualConTelefono.length} individual / ${gruposEnvioInfo.gruposConDestinatario} grupo(s))`
                        }
                      >
                        <span className="btn-wa-icon" aria-hidden="true">📋</span>
                      </button>
                    </>
                  )}
                  {hasPermission('crear_creditos') && (
                    <button
                      type="button"
                      className="btn-action-icon"
                      onClick={() => navigate('/recibos-grupo/nuevo')}
                      title="Recibo de cobro grupo"
                    >
                      <span aria-hidden="true">🧾👥</span>
                    </button>
                  )}
                  {hasPermission('editar_clientes') && (
                    <button
                      type="button"
                      className="btn-action-icon"
                      onClick={() => { setAbonoModo('porcentaje'); setAbonoValor(''); setShowModalAbono(true); }}
                      disabled={clientesSeleccionados.length === 0}
                      title={clientesSeleccionados.length === 0 ? 'Seleccione al menos un cliente' : 'Actualizar abono mensual de los clientes seleccionados'}
                    >
                      <span aria-hidden="true">💲</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          {clientesOrdenados.length > 0 && (
            <>
              <span className="toolbar-grupo--separador" aria-hidden="true" />
              <div className="clientes-toolbar-export">
                <ExportActions
                  onCsv={() => handleExportListado('csv')}
                  onExcel={() => handleExportListado('excel')}
                  onPrint={generarPDFListado}
                  printLabel="PDF"
                />
              </div>
            </>
          )}
        </div>
        <div className="clientes-listado-cuerpo">
        <div
          className={
            refrescandoClientes
              ? 'clientes-listado-tabla-area clientes-listado-tabla-area--refreshing'
              : 'clientes-listado-tabla-area'
          }
        >
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 44 }}>
                <input
                  type="checkbox"
                  checked={clientesActivos.length > 0 && clientesActivos.every(c => selectedIds.has(c.id))}
                  onChange={(e) => (e.target.checked ? seleccionarTodos() : deseleccionarTodos())}
                  title="Seleccionar todos los activos"
                />
              </th>
              {ordenColumnasVisible.map(colId => (
                <th
                  key={colId}
                  className="th-sortable"
                  onClick={() => handleSortColumn(colId)}
                  title={`Ordenar por ${getColumnLabel(colId)}. Clic de nuevo invierte el orden.`}
                >
                  <span>{getColumnLabel(colId)}</span>
                  {sortByColumn === colId && (
                    <span className="sort-indicator" aria-hidden="true">
                      {sortAsc ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clientesOrdenados.length === 0 ? (
              <tr>
                <td colSpan={2 + ordenColumnasVisible.length} className="no-data">No hay clientes que coincidan con el filtro</td>
              </tr>
            ) : (
              clientesOrdenados.map(cliente => (
                <tr key={cliente.id} className="table-row">
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(cliente.id)}
                      onChange={() => toggleSeleccion(cliente.id)}
                      disabled={!cliente.activo}
                      title={cliente.activo ? 'Seleccionar para enviar resumen' : 'Solo se pueden seleccionar clientes activos'}
                    />
                  </td>
                  {ordenColumnasVisible.map(colId => {
                    if (colId === 'cuit') return <td key={colId}><strong>{cliente.cuit}</strong></td>;
                    if (colId === 'razon_social') return (
                      <td key={colId}>
                        <div style={{ fontWeight: '500', color: '#2c3e50' }}>{cliente.razon_social}</div>
                        {cliente.email && (
                          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>{cliente.email}</div>
                        )}
                      </td>
                    );
                    if (colId === 'estado') return (
                      <td key={colId}>
                        <span className={`badge ${cliente.activo ? 'activo' : 'inactivo'}`}>
                          {cliente.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                    );
                    if (colId === 'tipo') return (
                      <td key={colId}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: '#e8f4f8',
                          color: '#2c3e50'
                        }}>
                          {cliente.tipo_nombre || '-'}
                        </span>
                      </td>
                    );
                    if (colId === 'grupo') return (
                      <td key={colId}>
                        {cliente.grupo_nombre ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: '#f0f4e8',
                            color: '#2c3e50'
                          }}>
                            {cliente.grupo_nombre}
                          </span>
                        ) : '-'}
                      </td>
                    );
                    if (colId === 'abono_mensual') return (
                      <td key={colId}>
                        {cliente.abono_mensual != null && cliente.abono_mensual !== ''
                          ? `$${parseFloat(cliente.abono_mensual).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    );
                    if (colId === 'empleados') return <td key={colId}>{cliente.cantidad_empleados != null && cliente.cantidad_empleados !== '' ? cliente.cantidad_empleados : '-'}</td>;
                    if (colId === 'direccion') return <td key={colId}>{cliente.direccion || '-'}</td>;
                    if (colId === 'provincia') return <td key={colId}>{cliente.provincia_nombre || '-'}</td>;
                    if (colId === 'localidad') return <td key={colId}>{cliente.localidad_nombre || '-'}</td>;
                    if (colId === 'telefono') return <td key={colId}>{cliente.telefono || '-'}</td>;
                    if (colId === 'condicion_impositiva') return <td key={colId}>{cliente.condicion_impositiva || '-'}</td>;
                    if (colId === 'ingresos_brutos') return <td key={colId}>{cliente.ingresos_brutos || '-'}</td>;
                    if (colId === 'drg') return <td key={colId}>{cliente.drg || '-'}</td>;
                    if (colId === 'dv') return <td key={colId}>{cliente.dv || '-'}</td>;
                    if (colId === 'iva_liquida') return <td key={colId}>{cliente.iva_liquida === 1 || cliente.iva_liquida === '1' ? 'Sí' : cliente.iva_liquida === 0 || cliente.iva_liquida === '0' ? 'No' : '-'}</td>;
                    if (colId === 'saldo') return (
                      <td key={colId} className={parseFloat(cliente.saldo || 0) > 0 ? 'saldo-deudor' : parseFloat(cliente.saldo || 0) < 0 ? 'saldo-acreedor' : ''}>
                        <strong>${parseFloat(cliente.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                      </td>
                    );
                    return <td key={colId}>-</td>;
                  })}
                  <td>
                    <div className="action-buttons">
                      {cliente.activo ? (
                        <>
                          {hasPermission('ver_movimientos') && (
                            <Link to={`/clientes/${cliente.id}/movimientos`} className="btn-action-icon" title="Ver movimientos">
                              <span aria-hidden="true">📋</span>
                            </Link>
                          )}
                          {hasPermission('crear_debitos') && (
                            <Link
                              to="/movimientos/nuevo"
                              state={{ clienteId: cliente.id }}
                              className="btn-action-icon"
                              title="Nuevo movimiento (débito)"
                            >
                              <span aria-hidden="true">💵</span>
                            </Link>
                          )}
                          {(hasPermission('crear_creditos') && hasPermission('crear_debitos')) && (
                            <Link
                              to="/movimientos/nuevo"
                              state={{ clienteId: cliente.id, nuevoRecibo: true }}
                              className="btn-action-icon"
                              title="Nuevo recibo de cobro"
                            >
                              <span aria-hidden="true">🧾</span>
                            </Link>
                          )}
                          {(hasPermission('crear_debitos')) && (
                            <Link
                              to="/movimientos/nuevo"
                              state={{ clienteId: cliente.id, nuevoPagoCliente: true }}
                              className="btn-action-icon"
                              title="Pago a cuenta (egreso desde fondos)"
                            >
                              <span aria-hidden="true">💸</span>
                            </Link>
                          )}
                          {hasPermission('editar_clientes') && (
                            <Link to={`/clientes/editar/${cliente.id}`} className="btn-action-icon btn-action-edit" title="Editar cliente">
                              <span aria-hidden="true">✏️</span>
                            </Link>
                          )}
                        </>
                      ) : (
                        hasPermission('editar_clientes') && (
                          <button type="button" onClick={() => handleToggleActivo(cliente)} className="btn-action-icon btn-action-activar" title="Activar cliente para poder usarlo en movimientos">
                            <span aria-hidden="true">▶️</span>
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
          {refrescandoClientes && (
            <div className="clientes-listado-refresh-overlay" role="status" aria-live="polite">
              <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
              <span className="clientes-listado-refresh-label">Actualizando…</span>
            </div>
          )}
        </div>
        </div>
      </div>

      {showModalWhatsApp && (
        <div
          className="modal-overlay"
          onClick={() => setShowModalWhatsApp(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '24px', borderRadius: '8px', maxWidth: '560px', width: '90%',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '90vh', overflow: 'auto'
            }}
          >
            <h2>Enviar saldo por WhatsApp</h2>
            {puedeModoGrupo && (
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="modo-envio-saldo" style={{ display: 'block', marginBottom: 4 }}>Modo de envío</label>
                <select
                  id="modo-envio-saldo"
                  value={modoEnvioSaldo}
                  onChange={(e) => setModoEnvioSaldo(e.target.value)}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="individual">Individual (un mensaje por cliente destinatario)</option>
                  <option value="grupo">Por grupo (consolidado a destinatarios del grupo)</option>
                </select>
              </div>
            )}
            <WhatsAppServicioPanel
              useBaileys={waUseBaileys}
              onToggleUseBaileys={handleToggleWaBaileys}
              status={waStatus}
              loading={waStatusLoading}
              onRefresh={refreshWaStatus}
              sendRateProfile={waSendRateProfile}
              onSendRateProfileChange={setWaSendRateProfile}
              estimateCount={modoEnvioSaldo === 'grupo' ? gruposEnvioInfo.gruposConDestinatario : clientesIndividualConTelefono.length}
            />
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="fecha-saldo-wa" style={{ display: 'block', marginBottom: 4 }}>Saldo al (fecha)</label>
              <input
                id="fecha-saldo-wa"
                type="date"
                value={fechaSaldoWa}
                onChange={(e) => setFechaSaldoWa(e.target.value)}
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
              />
              <small style={{ color: '#7f8c8d' }}>Los saldos se recalculan a esta fecha antes del envío. Variable: {'{fecha_saldo}'}</small>
            </div>
            {modoEnvioSaldo === 'grupo' ? (
              <>
                <p style={{ color: '#666', marginBottom: '12px', lineHeight: 1.5 }}>
                  Se enviará un resumen consolidado por cada grupo ({gruposEnvioInfo.gruposConDestinatario} grupo(s)),
                  solo a los <strong>{clientesGrupoDestinatariosSeleccionados.length}</strong> destinatario(s) seleccionados
                  marcados en <Link to="/grupos">Clientes → Grupos → Configurar envío</Link>.
                </p>
                {resumenExclusionGrupoModoGrupo() && (
                  <p style={{ color: '#856404', background: '#fff3cd', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                    {resumenExclusionGrupoModoGrupo()}
                  </p>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <label>Plantillas de grupo</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                    <select
                      value={plantillaGrupoSaldoId}
                      onChange={(e) => seleccionarPlantillaGrupoSaldo(e.target.value)}
                      style={{ padding: '8px 12px', minWidth: '180px' }}
                    >
                      <option value="">(Sin selección)</option>
                      {plantillasGrupo.map((p) => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                    <button type="button" className="btn-edit" onClick={() => guardarPlantillaGrupo(mensajeGrupoSaldo, setPlantillaGrupoSaldoId)}>Guardar como nueva</button>
                    <button type="button" className="btn-edit" onClick={() => actualizarPlantillaGrupo(plantillaGrupoSaldoId, mensajeGrupoSaldo)} disabled={!plantillaGrupoSaldoId}>Actualizar</button>
                    <button type="button" className="btn-delete" onClick={() => eliminarPlantillaGrupo(plantillaGrupoSaldoId, setPlantillaGrupoSaldoId, setMensajeGrupoSaldo)} disabled={!plantillaGrupoSaldoId}>Eliminar</button>
                  </div>
                  <PlantillaImagenEditor
                    plantilla={plantillasGrupo.find((p) => p.id === plantillaGrupoSaldoId)}
                    onChange={loadPlantillasGrupo}
                    useBaileys={waUseBaileys}
                    canEdit={hasPermission('editar_clientes')}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label htmlFor="mensaje-grupo-saldo">Mensaje (variables: {'{grupo_nombre}'}, {'{saldo_consolidado}'}, {'{resumen_grupo}'}, {'{destinatario_razon_social}'}, etc.)</label>
                  <textarea
                    id="mensaje-grupo-saldo"
                    value={mensajeGrupoSaldo}
                    onChange={(e) => setMensajeGrupoSaldo(e.target.value)}
                    rows={6}
                    style={{ width: '100%', padding: '10px', marginTop: '6px', boxSizing: 'border-box' }}
                  />
                  <small style={{ color: '#7f8c8d' }}>{'{resumen_grupo}'} incluye el detalle según el formato configurado en el grupo.</small>
                </div>
                {(previewGrupoSaldo || loadingPreviewGrupo) && (
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#f5f5f5', borderRadius: '6px' }}>
                    <strong>Vista previa (primer grupo):</strong>
                    {loadingPreviewGrupo ? (
                      <p style={{ marginTop: 6, fontSize: 13 }}>Generando vista previa…</p>
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px', fontSize: '13px' }}>{previewGrupoSaldo}</pre>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '12px' }}>
                  Se enviará a <strong>{clientesIndividualConTelefono.length}</strong> cliente(s) sin grupo con teléfono.
                  {waUseBaileys ? ' (un mensaje a la vez con pausas anti-bloqueo).' : ' (se abrirá una ventana por cada cliente).'}
                </p>
                {resumenExclusionGrupoIndividual() && (
                  <p style={{ color: '#856404', background: '#fff3cd', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                    {resumenExclusionGrupoIndividual()}
                  </p>
                )}
              </>
            )}
            {waEnvioProgreso && (
              <p style={{ color: '#1565c0', marginBottom: 12, fontSize: 13 }}>{waEnvioProgreso}</p>
            )}
            {modoEnvioSaldo === 'individual' && (
            <>
            <div style={{ marginBottom: '12px' }}>
              <label>Plantillas guardadas</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                <select
                  value={plantillaSeleccionadaId}
                  onChange={(e) => seleccionarPlantilla(e.target.value)}
                  style={{ padding: '8px 12px', minWidth: '180px' }}
                >
                  <option value="">(Sin selección)</option>
                  {plantillas.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
                <button type="button" className="btn-edit" onClick={guardarComoNueva}>Guardar como nueva</button>
                <button type="button" className="btn-edit" onClick={actualizarPlantilla} disabled={!plantillaSeleccionadaId}>Actualizar</button>
                <button type="button" className="btn-delete" onClick={eliminarPlantilla} disabled={!plantillaSeleccionadaId}>Eliminar</button>
              </div>
              <PlantillaImagenEditor
                plantilla={plantillas.find((p) => p.id === plantillaSeleccionadaId)}
                onChange={loadPlantillas}
                useBaileys={waUseBaileys}
                canEdit={hasPermission('editar_clientes')}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="mensaje-resumen">Mensaje (variables: {'{razon_social}'}, {'{cuit}'}, {'{saldo}'}, {'{fecha_saldo}'}, {'{telefono}'}, {'{tipo_nombre}'}, etc.)</label>
              <textarea
                id="mensaje-resumen"
                value={mensajePersonalizado}
                onChange={(e) => setMensajePersonalizado(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '10px', marginTop: '6px', boxSizing: 'border-box' }}
              />
            </div>
            {(previewSaldoWa || clientesIndividualConTelefono[0]) && (
              <div style={{ marginBottom: '12px', padding: '10px', background: '#f5f5f5', borderRadius: '6px' }}>
                <strong>Vista previa (primer cliente):</strong>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px', fontSize: '13px' }}>
                  {aplicarPlantillaResumen(
                    previewSaldoWa || clientesIndividualConTelefono[0],
                    mensajePersonalizado,
                    fechaSaldoWa
                  )}
                </pre>
              </div>
            )}
            </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" className="btn-delete" onClick={() => setShowModalWhatsApp(false)} disabled={enviandoMasivoWa}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                onClick={iniciarEnvioMasivo}
                disabled={enviandoMasivoWa || (modoEnvioSaldo === 'individual' && clientesIndividualConTelefono.length === 0) || (modoEnvioSaldo === 'grupo' && gruposEnvioInfo.gruposConDestinatario === 0)}
              >
                {enviandoMasivoWa ? 'Enviando…' : modoEnvioSaldo === 'grupo'
                  ? `Enviar por grupo (${gruposEnvioInfo.gruposConDestinatario})`
                  : `Iniciar envío masivo (${clientesIndividualConTelefono.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalWhatsAppMovimientos && (
        <div
          className="modal-overlay"
          onClick={() => !enviandoMasivoMov && setShowModalWhatsAppMovimientos(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '24px', borderRadius: '8px', maxWidth: '560px', width: '90%',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '90vh', overflow: 'auto'
            }}
          >
            <h2>Enviar resumen de movimientos por WhatsApp</h2>
            {puedeModoGrupo && (
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="modo-envio-mov" style={{ display: 'block', marginBottom: 4 }}>Modo de envío</label>
                <select
                  id="modo-envio-mov"
                  value={modoEnvioMov}
                  onChange={(e) => setModoEnvioMov(e.target.value)}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="individual">Individual (un mensaje por cliente destinatario)</option>
                  <option value="grupo">Por grupo (consolidado a destinatarios del grupo)</option>
                </select>
              </div>
            )}
            <WhatsAppServicioPanel
              useBaileys={waUseBaileys}
              onToggleUseBaileys={handleToggleWaBaileys}
              status={waStatus}
              loading={waStatusLoading}
              onRefresh={refreshWaStatus}
              sendRateProfile={waSendRateProfile}
              onSendRateProfileChange={setWaSendRateProfile}
              estimateCount={modoEnvioMov === 'grupo' ? gruposEnvioInfo.gruposConDestinatario : clientesIndividualConTelefono.length}
            />
            {modoEnvioMov === 'grupo' ? (
              <>
                <p style={{ color: '#666', marginBottom: '12px', lineHeight: 1.5 }}>
                  Resumen de movimientos consolidado por grupo ({gruposEnvioInfo.gruposConDestinatario} grupo(s)),
                  enviado solo a los <strong>{clientesGrupoDestinatariosSeleccionados.length}</strong> destinatario(s) seleccionados.
                </p>
                {resumenExclusionGrupoModoGrupo() && (
                  <p style={{ color: '#856404', background: '#fff3cd', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                    {resumenExclusionGrupoModoGrupo()}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <label>Desde</label>
                    <input type="date" value={fechaDesdeMov} onChange={e => setFechaDesdeMov(e.target.value)} style={{ marginLeft: 6, padding: 6 }} />
                  </div>
                  <div>
                    <label>Hasta</label>
                    <input type="date" value={fechaHastaMov} onChange={e => setFechaHastaMov(e.target.value)} style={{ marginLeft: 6, padding: 6 }} />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label>Plantillas de grupo</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                    <select
                      value={plantillaGrupoMovId}
                      onChange={(e) => seleccionarPlantillaGrupoMov(e.target.value)}
                      style={{ padding: '8px 12px', minWidth: '180px' }}
                    >
                      <option value="">(Sin selección)</option>
                      {plantillasGrupo.map((p) => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                    <button type="button" className="btn-edit" onClick={() => guardarPlantillaGrupo(mensajeGrupoMov, setPlantillaGrupoMovId)}>Guardar como nueva</button>
                    <button type="button" className="btn-edit" onClick={() => actualizarPlantillaGrupo(plantillaGrupoMovId, mensajeGrupoMov)} disabled={!plantillaGrupoMovId}>Actualizar</button>
                    <button type="button" className="btn-delete" onClick={() => eliminarPlantillaGrupo(plantillaGrupoMovId, setPlantillaGrupoMovId, setMensajeGrupoMov)} disabled={!plantillaGrupoMovId}>Eliminar</button>
                  </div>
                  <PlantillaImagenEditor
                    plantilla={plantillasGrupo.find((p) => p.id === plantillaGrupoMovId)}
                    onChange={loadPlantillasGrupo}
                    useBaileys={waUseBaileys}
                    canEdit={hasPermission('editar_clientes')}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label htmlFor="mensaje-grupo-mov">Mensaje (variables: {'{grupo_nombre}'}, {'{resumen_grupo}'}, {'{fecha_desde}'}, {'{fecha_hasta}'}, {'{destinatario_razon_social}'}, etc.)</label>
                  <textarea
                    id="mensaje-grupo-mov"
                    value={mensajeGrupoMov}
                    onChange={(e) => setMensajeGrupoMov(e.target.value)}
                    rows={6}
                    style={{ width: '100%', padding: '10px', marginTop: '6px', boxSizing: 'border-box' }}
                  />
                </div>
                {(previewGrupoMov || loadingPreviewGrupo) && (
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#f5f5f5', borderRadius: '6px' }}>
                    <strong>Vista previa (primer grupo):</strong>
                    {loadingPreviewGrupo ? (
                      <p style={{ marginTop: 6, fontSize: 13 }}>Generando vista previa…</p>
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px', fontSize: '13px' }}>{previewGrupoMov}</pre>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '12px' }}>
                  Se enviará a <strong>{clientesIndividualConTelefono.length}</strong> cliente(s) sin grupo. Variables: {'{lista_movimientos}'}, {'{saldo}'}, etc.
                </p>
                {resumenExclusionGrupoIndividual() && (
                  <p style={{ color: '#856404', background: '#fff3cd', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                    {resumenExclusionGrupoIndividual()}
                  </p>
                )}
              </>
            )}
            {waEnvioProgreso && (
              <p style={{ color: '#1565c0', marginBottom: 12, fontSize: 13 }}>{waEnvioProgreso}</p>
            )}
            {modoEnvioMov === 'individual' && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div>
                <label>Desde</label>
                <input type="date" value={fechaDesdeMov} onChange={e => setFechaDesdeMov(e.target.value)} style={{ marginLeft: 6, padding: 6 }} />
              </div>
              <div>
                <label>Hasta</label>
                <input type="date" value={fechaHastaMov} onChange={e => setFechaHastaMov(e.target.value)} style={{ marginLeft: 6, padding: 6 }} />
              </div>
            </div>
            )}
            {modoEnvioMov === 'individual' && plantillas.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label>Plantilla</label>
                <select value={plantillaSeleccionadaIdMovimientos} onChange={e => seleccionarPlantillaMovimientos(e.target.value)} style={{ marginLeft: 8, padding: '8px 12px', minWidth: 180 }}>
                  {plantillas.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
                <PlantillaImagenEditor
                  plantilla={plantillas.find((p) => p.id === plantillaSeleccionadaIdMovimientos)}
                  onChange={loadPlantillas}
                  useBaileys={waUseBaileys}
                  canEdit={hasPermission('editar_clientes')}
                />
              </div>
            )}
            {modoEnvioMov === 'individual' && (
            <div style={{ marginBottom: '12px' }}>
              <label>Mensaje</label>
              <textarea
                value={mensajePersonalizadoMovimientos}
                onChange={e => setMensajePersonalizadoMovimientos(e.target.value)}
                rows={10}
                style={{ width: '100%', padding: 10, marginTop: 6, boxSizing: 'border-box' }}
              />
            </div>
            )}
            {modoEnvioMov === 'individual' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input type="checkbox" checked={guardarEnHistorialMov} onChange={e => setGuardarEnHistorialMov(e.target.checked)} />
              Guardar en historial de envíos
            </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-delete" onClick={() => setShowModalWhatsAppMovimientos(false)} disabled={enviandoMasivoMov}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                onClick={enviarResumenMovimientosMasivo}
                disabled={enviandoMasivoMov || (modoEnvioMov === 'individual' && clientesIndividualConTelefono.length === 0) || (modoEnvioMov === 'grupo' && gruposEnvioInfo.gruposConDestinatario === 0)}
              >
                {enviandoMasivoMov
                  ? waUseBaileys
                    ? 'Preparando y encolando…'
                    : `Enviando... (${clientesIndividualConTelefono.length} ventanas)`
                  : modoEnvioMov === 'grupo'
                    ? `Enviar por grupo (${gruposEnvioInfo.gruposConDestinatario})`
                    : `Iniciar envío masivo (${clientesIndividualConTelefono.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalAbono && (
        <div
          className="modal-overlay"
          onClick={() => !actualizandoAbono && setShowModalAbono(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '24px', borderRadius: '8px', maxWidth: '520px', width: '90%',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '90vh', overflow: 'auto'
            }}
          >
            <h2>Ajustar abono mensual</h2>
            <p style={{ color: '#666', marginBottom: '12px', fontSize: 14 }}>
              Se aplicará un ajuste al abono mensual de <strong>{clientesSeleccionados.length}</strong> cliente(s) seleccionados.
              Puede ser un porcentaje o un valor fijo, positivo o negativo.
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>Tipo de ajuste</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="abonoModo"
                    value="porcentaje"
                    checked={abonoModo === 'porcentaje'}
                    onChange={() => setAbonoModo('porcentaje')}
                  />
                  <span>Porcentaje (%)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="abonoModo"
                    value="fijo"
                    checked={abonoModo === 'fijo'}
                    onChange={() => setAbonoModo('fijo')}
                  />
                  <span>Valor fijo ($)</span>
                </label>
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>
                {abonoModo === 'porcentaje' ? 'Porcentaje de aumento/disminución' : 'Importe a sumar/restar'}
              </label>
              <input
                type="number"
                value={abonoValor}
                onChange={(e) => setAbonoValor(e.target.value)}
                step="0.01"
                style={{ width: '100%', padding: '8px 10px', boxSizing: 'border-box' }}
                placeholder={abonoModo === 'porcentaje' ? 'Ej: 10 para +10%, -5 para -5%' : 'Ej: 500 para sumar $500, -300 para restar $300'}
              />
            </div>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              Ejemplos:<br />
              - Porcentaje: 10 = aumento del 10%; -5 = disminución del 5%.<br />
              - Valor fijo: 500 = suma $500 al abono actual; -300 = resta $300.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="btn-delete"
                onClick={() => setShowModalAbono(false)}
                disabled={actualizandoAbono}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={aplicarAjusteAbono}
                disabled={actualizandoAbono}
              >
                {actualizandoAbono ? 'Aplicando...' : 'Aplicar a seleccionados'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;

